import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, Save, Edit } from 'lucide-react';
import { BasicInfoSection } from './sections/BasicInfoSection';
import { ServiceAreasSection } from './sections/ServiceAreasSection';
import { CapabilitiesSection } from './sections/CapabilitiesSection';
import { CertificationsSection } from './sections/CertificationsSection';
import { CarveoutSection } from './sections/CarveoutSection';

interface EnhancedPEOProviderProfileProps {
  providerInfo: any;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  form: any;
  setForm: React.Dispatch<React.SetStateAction<any>>;
  onSave: (e: React.FormEvent) => void;
  saving: boolean;
}

export const EnhancedPEOProviderProfile = ({
  providerInfo,
  isEditing,
  setIsEditing,
  form,
  setForm,
  onSave,
  saving,
}: EnhancedPEOProviderProfileProps) => {
  const [activeTab, setActiveTab] = useState('basic');

  if (!providerInfo) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Your PEO Provider Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No Provider Profile</AlertTitle>
            <AlertDescription>
              Your provider profile is not set up yet. Please contact support if you believe this is an error.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!isEditing) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Your PEO Provider Profile</CardTitle>
          <Button 
            variant="outline" 
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2"
          >
            <Edit className="h-4 w-4" />
            Edit Profile
          </Button>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="areas">Service Areas</TabsTrigger>
              <TabsTrigger value="capabilities">Capabilities</TabsTrigger>
              <TabsTrigger value="certifications">Certifications</TabsTrigger>
              <TabsTrigger value="carveout">Carve-outs</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="mt-6">
              <BasicInfoSection providerInfo={providerInfo} readonly />
            </TabsContent>

            <TabsContent value="areas" className="mt-6">
              <ServiceAreasSection providerInfo={providerInfo} readonly />
            </TabsContent>

            <TabsContent value="capabilities" className="mt-6">
              <CapabilitiesSection providerInfo={providerInfo} readonly />
            </TabsContent>

            <TabsContent value="certifications" className="mt-6">
              <CertificationsSection providerInfo={providerInfo} readonly />
            </TabsContent>

            <TabsContent value="carveout" className="mt-6">
              <CarveoutSection providerInfo={providerInfo} readonly />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Edit Your PEO Provider Profile</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSave} className="space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="areas">Service Areas</TabsTrigger>
              <TabsTrigger value="capabilities">Capabilities</TabsTrigger>
              <TabsTrigger value="certifications">Certifications</TabsTrigger>
              <TabsTrigger value="carveout">Carve-outs</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="mt-6">
              <BasicInfoSection 
                providerInfo={providerInfo} 
                form={form} 
                setForm={setForm} 
                saving={saving} 
              />
            </TabsContent>

            <TabsContent value="areas" className="mt-6">
              <ServiceAreasSection 
                providerInfo={providerInfo} 
                form={form} 
                setForm={setForm} 
                saving={saving} 
              />
            </TabsContent>

            <TabsContent value="capabilities" className="mt-6">
              <CapabilitiesSection 
                providerInfo={providerInfo} 
                form={form} 
                setForm={setForm} 
                saving={saving} 
              />
            </TabsContent>

            <TabsContent value="certifications" className="mt-6">
              <CertificationsSection 
                providerInfo={providerInfo} 
                form={form} 
                setForm={setForm} 
                saving={saving} 
              />
            </TabsContent>

            <TabsContent value="carveout" className="mt-6">
              <CarveoutSection 
                providerInfo={providerInfo} 
                form={form} 
                setForm={setForm} 
                saving={saving} 
              />
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsEditing(false)} 
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex items-center gap-2"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Save className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};